import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useExcelExportFormat from '../../../../ui/table/hooks/useExcelExportFormat';
import useBoardContentWidgetData from '../../../hooks/useBoardContentWidgetData';
import useBoardContentWidget from '../../../hooks/useBoardContentWidget';
import { useInternationalization } from '@progress/kendo-react-intl';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MODAL, POPUP } from '../../../../../redux/types';
import FilterCtx from '../../../../../context/filterContext/filterCtx';
import useTranslate from '../../../../../common/hooks/useTranslate';
import replaceDotsWithUnderscores from '../../../../../common/utility/replaceDotsWithUnderscores';

import { CompositeFilterDescriptor, process, SortDescriptor } from '@progress/kendo-data-query';
import { IBoardMeasure } from '../../../types/IBoardMeasure';
import { IBoardNote } from '../../../types/IBoardNote';
import { WidgetProps } from '../../../types/IWidgetProps';
import { ICurrency } from '../../../types/ICurrency';
import { IGridPreparedData } from '../grid/types/IGridPreparedData';
import { IBoardObjectNode } from '../../../types/IBoardObjectNode';
import { IGridDefinitionColumn } from '../grid/types/IGridDefinitionColumn';
import { ISummarySetup } from '../summary/types/ISummarySetup';
import { IWidgetData } from '../../../types/IWidgetData';
import { IGridPeriodColumn } from './types/IGridPeriodColumn';
import { ICashflow } from '../../../types/ICashflow';

import WidgetWrapper from '../widgetWrapper';
import ContentLoading from '../../../../ui/loaders/contentLoading/contentLoading';
import Icon from '../../../../ui/icon/icon';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import CashflowCell from '../cashflow/cells/cashflowCell/cashflowCell';
import NumericCell from '../../cells/numericCell/numericCell';
import { ExcelExport } from '@progress/kendo-react-excel-export';

import '../grid/gridWidget.scss';
import './annualGridWidget.scss';

const AnnualGridWidget: React.FC<WidgetProps> = ({
                                                   widget,
                                                   selectedObject,
                                                   selectedPeriod,
                                                   selectedCurrency,
                                                   boardData,
                                                   scenarios,
                                                   notes,
                                                   customisation,
                                                   content,
                                                   moveWidget,
                                                   removeWidget,
                                                   handleLink,
                                                   homeContent,
                                                   widgetView,
                                                   handlePinWidget,
                                                   handleUnpinWidget,
                                                   handleUnpinTrigger,
                                                   unpinId,
                                                 }) => {
  const [localCurrency, setLocalCurrency] = useState<ICurrency>();
  const [gridData, setGridData] = useState<IGridPreparedData[]>([]);
  const [nodes, setNodes] = useState<IBoardObjectNode[]>([]);
  const [widgetCurrencies, setWidgetCurrencies] = useState<ICurrency[]>([]);
  const [gridColumns, setGridColumns] = useState<IGridDefinitionColumn[]>([]);
  const [gridSetup, setGridSetup] = useState<ISummarySetup>();
  const [gridNotes, setGridNotes] = useState<IBoardNote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [objectValueState, setObjectValueState] = useState<Map<number, any> | null>(null);
  const [resultState, setResultState] = useState<any>({});
  const [periodicityColumns, setPeriodicityColumns] = useState<IGridPeriodColumn[]>([]);

  const excelFormat = useExcelExportFormat();
  const { defineNodes, generateCurrencies, fetchNodeData } = useBoardContentWidgetData();
  const { widgetSetup } = useBoardContentWidget();
  const exportRef = useRef<any>(null);
  const dispatch = useDispatch();

  const intl = useInternationalization();
  const params = useParams();
  const { t } = useTranslate();

  const initialFilter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [],
  };

  const filterCtx = useContext(FilterCtx);

  const _replaceRowAmount = (field: string): string => {
    return field.replace(/_rowAmount$/, '');
  };

  const _idAndTypeFromField = (fieldName: string): any => {
    let split = fieldName.slice(3).split('_');

    if (split.length < 2) return null;

    return {
      id: parseInt(split[1]),
      type: split[0],
      nodeId: split.length === 4 ? split[3] : null,
    };
  };

  const getCashflowInfo = (dataItem: any, fieldName: string): ICashflow | undefined => {
    fieldName = _replaceRowAmount(fieldName);

    let idAndType = _idAndTypeFromField(fieldName);
    if (!idAndType) return;

    let measure = boardData?.measures.find((m) => m.id === idAndType.id);

    if (measure && measure.id) {
      let objectVal = objectValueState?.get(dataItem['periodId']);
      let key = `${measure.id}_${idAndType.nodeId}`;
      return {
        nodeId: objectVal.map[key || ''].nodeId,
        valueId: objectVal.map[key || ''].valueId,
        valueInfo: {
          name: dataItem.name,
          measure: measure,
          amount: dataItem[fieldName] || 0,
        },
      };
    } else return;
  };

  const openCashflowDialog = (dataItem, field, columnName, boardId, investorId) => {
    let item = dataItem[field];
    let cashFlowInfo = getCashflowInfo(dataItem, field);

    dispatch({
      type: MODAL.SWITCH_CASH_FLOW_MODAL,
      payload: {
        dataItem: dataItem,
        cashFlowInfo: cashFlowInfo,
        investorObjectId: investorId,
        columnName: columnName,
        value: intl.toString(item.rowAmount, (item.isPercent ? 'p' : 'n') + item.scale),
        boardId: boardId,
        selectedCurrency: localCurrency,
      },
    });
  };

  const [dataState, setDataState] = useState<any>(filterCtx.gridWidgetFilterState);

  const initialDataState = {
    ...filterCtx.gridWidgetFilterState,
    take: undefined,
    sort: [
      {
        field: 'periodName',
        dir: 'desc',
      },
    ],
    filter: initialFilter,
    skip: 0,
  };

  let objectValueMap = new Map<number, any>();

  const quarterOrder = ['Q1', 'Q2', 'Q3', 'Q4'];
  const sortByQuarter = (a: string, b: string) =>
    quarterOrder.indexOf(a) - quarterOrder.indexOf(b);

  const measuresForFetch = (setup: ISummarySetup) => {
    let measures: IBoardMeasure[] = [];
    setup?.definition?.forEach((def) => {
      if (def.measureId && !measures.find((m) => m.id === def.measureId)) {
        measures.push({ id: def.measureId });
      }
    });
    return measures;
  };

  const sortCompare = (itemA: any, itemB: any, field: string, dir: string) => {
    let adjust = dir === 'asc' ? 1 : -1;
    let result = 0;
    if (itemA[field] < itemB[field]) result = -1;
    if (itemA[field] > itemB[field]) result = 1;
    return result * adjust;
  };

  const sortDescriptors = (sort: SortDescriptor[]) => {
    return sort.map((item) => {
      if (item.field.startsWith('ID')) {
        let colId = item.field.substring(2, item.field.indexOf('_'));

        let column = gridColumns.find((col) => col.columnId === colId);
        if (column && column.type === 'measure') {
          return {
            ...item,
            compare: (itemA: any, itemB: any) => sortCompare(itemA, itemB, item.field, item.dir || 'asc'),
          };
        }
      }
      return item;
    });
  };

  const onDataStateChange = useCallback(
    (e) => {
      if (e.dataState.sort) e.dataState.sort = sortDescriptors(e.dataState.sort);

      setDataState(e.dataState);
      setResultState(process(gridData, e.dataState));
    },
    [gridData],
  );

  const getPeriodLabel = (periodName?: string) => {
    if (!periodName) return undefined;
    const parts = periodName.trim().split(/\s+/);
    return parts[parts.length - 1];
  };

  const getFieldNameFor = (measureId: number, periodLabel: string, nodeId: number) =>
    `ID_measure_${measureId}_${periodLabel}_${nodeId}`;

  const getTotalFieldNameFor = (measureId: number) =>
    `TOTAL_measure_${measureId}`;

  const getFieldNamesForMeasure = (measureId: number, nodeId: number, setup: ISummarySetup, periodName: string) => {
    let fieldNames: string[] = [];
    setup?.definition?.forEach((definition) => {
      if (definition.measureId === measureId) fieldNames.push(getFieldNameFor(measureId, getPeriodLabel(periodName), nodeId));
    });
    return fieldNames;
  };

  const prepRowAnnual = (preparedData: any[], row: any, field: string, valueField: string) => {
    let objectValue = objectValueMap.get(row['periodId']);

    if (objectValue) {
      objectValue.row[field] = row[valueField];
    } else {
      objectValue = {
        row: {
          periodId: row.periodId,
          periodName: row.periodName,
          startDate: row.startDate,
          endDate: row.endDate,
        },
        map: {},
      };

      objectValue.row[field] = row[valueField];

      objectValueMap.set(row['periodId'], objectValue);
      setObjectValueState(objectValueMap);

      preparedData.push(objectValue.row);
    }

    return objectValue;
  };

  const prepareAnnualGridData = (
    setup: ISummarySetup,
    measureValues: IWidgetData[],
    nodes: IBoardObjectNode[],
  ): IGridPreparedData[] => {
    let preparedData: IGridPreparedData[] = [];
    objectValueMap.clear();
    setObjectValueState(null);

    measureValues.forEach((row) => {
      let measure = boardData?.measures?.find((m) => m.id === row.measureId);
      if (measure) {
        let valueField = measure.dataType === 'date' ? 'dateValue' : 'amount';
        let node = nodes.find((item) => {
          return item.id === row.nodeId;
        });

        if (node) {
          let fields = getFieldNamesForMeasure(measure.id, node.id, setup, row.periodName);
          fields.forEach((field) => {
            let objectValue = prepRowAnnual(preparedData, row, field, valueField);

            if (objectValue) {
              let key = measure?.id + '_' + node?.id;

              objectValue.map[key] = {
                nodeId: node?.id,
                scenarioId: node?.scenarioId,
                valueId: row.id,
                periodId: row.periodId,
              };

              objectValue.row[field] = {
                rowAmount: valueField === 'dateValue' ? intl.formatDate(new Date(objectValue.row[field]), 'd') : objectValue.row[field],
                isPercent: measure?.isPercent,
                scale: measure?.scale,
                dataType: measure?.dataType,
              };
              objectValue.row[`measureId`] = measure?.id;
              objectValue.row[`nodeId`] = node?.id;

              let totalField = getTotalFieldNameFor(measure?.id);

              objectValue.row[totalField] = {
                rowAmount: 0,
                isPercent: measure?.isPercent,
                scale: measure?.scale,
                dataType: measure?.dataType,
              };
            }
          });
        }
      }
    });

    return preparedData;
  };

  const handleLocalCurrencyChange = (e) => {
    setLocalCurrency(e.value);

    if (gridSetup && selectedPeriod) {
      setLoading(true);

      fetchNodeData({
        ...widget,
        investorObjectId: widget.investorObjectId || parseInt(params.investorId),
      }, nodes, measuresForFetch(gridSetup), selectedPeriod, e.value.currency || '', false).then((data) => {
        return {
          measureValues: data,
          staticValues: [],
        };
      }).then((res) => {
        let data = prepareAnnualGridData(gridSetup, res?.measureValues, nodes);
        setGridData(data);
        setLoading(false);
      })
        .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  };

  const extractMeasureId = (key: string) => {
    const m = key.match(/measure_(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  };

  const onExcelExport = () => {
    if (exportRef && exportRef.current) {
      const workbook = exportRef.current.workbookOptions();
      const rows = workbook.sheets[0].rows;

      const objectRow = {
        cells: [
          { value: selectedObject?.name },
        ],
      };

      const currencyRow = {
        cells: [
          { value: localCurrency?.currency },
        ],
      };

      rows?.filter((r) => r.type === 'data').forEach((row: any, rowIndex) => {
        row.cells.forEach((cell: any) => {
          let dataPoint = resultState?.data && resultState.data[rowIndex];
          const key = dataPoint && Object.keys(dataPoint).find(key => dataPoint[key] === cell.value);
          const measureId = key && extractMeasureId(key);
          const measure = measureId && boardData.measures?.find((m) => m.id === measureId);
          const column = measureId && gridColumns.find((c) => c.measureId === measureId);
          if (cell.value && measure) {
            cell.value = cell.value.rowAmount;
            cell.format = measure?.isPercent
              ? excelFormat.percentFormat(column?.scale || measure?.scale || 0)
              : excelFormat.numberFormat(column?.scale || measure?.scale || 0);
          }
        });
      });

      const rowSplit = 3;
      rows?.unshift(objectRow, currencyRow);
      let freeze = workbook.sheets[0].freezePane || {};
      workbook.sheets[0].freezePane = { ...freeze, rowSplit: rowSplit };
      exportRef.current.save(workbook);
    }
  };

  const filterNotes = (notes: IBoardNote[]) => {
    let gNotes: IBoardNote[] = [];
    const currentColumns = gridSetup?.definition;
    for (let i = 0; i < notes.length; i++) {
      const measures = notes[i] && notes[i].measures;
      if (measures) {
        for (let j = 0; j < measures.length; j++) {
          let measureId = measures[j].measureId;
          currentColumns?.forEach((column) => {
            if (column.measureId === measureId && !gNotes.includes(notes[i])) {
              gNotes.push(notes[i]);
            }
          });
        }
      }
    }

    setGridNotes(gNotes);

    if (gNotes.length > 0 && gridSetup?.singlePeriod && currentColumns) extractNotesForGridColumns(gNotes, currentColumns);
  };

  const extractNotesForGridColumns = (notes: IBoardNote[], columns: IGridDefinitionColumn[]) => {
    setGridColumns(
      columns.map((column) => {
        let itemNotes: IBoardNote[] = [];
        const measureId = column.measureId;
        notes.forEach((note) => {
          const noteMeasureIds = note?.measures?.map((m) => m.measureId);
          if (measureId && noteMeasureIds?.includes(measureId)) {
            itemNotes.push(note);
          }
        });
        return { ...column, notes: itemNotes, hasNotes: itemNotes.length > 0 };
      }),
    );
  };

  const extractPeriodColumns = (data: IGridPreparedData[]): IGridPeriodColumn[] => {
    const byNode: any = {};
    for (const item of data) {
      const label = getPeriodLabel(item.periodName);
      if (!label) continue;
      byNode[item.nodeId] = byNode[item.nodeId] || [];
      const arr = byNode[item.nodeId];
      if (!arr.includes(label)) arr.push(label);
    }

    return Object.entries(byNode).flatMap(([id, labels]: [string, string[]]) =>
      labels.sort(sortByQuarter).map(periodLabel => ({ periodLabel, nodeId: parseInt(id) })),
    );
  };

  const yearFromIso = (d: string) => new Date(d).getFullYear();

  const extractTotalMeasureId = (key: string) => {
    const m = key.match(/^TOTAL_measure_(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  };

  const transformData = (data: IGridPreparedData[]) => {
    const byYear: any = {};

    for (const item of data) {
      const year = yearFromIso(item.startDate);
      const nodeId = String(item.nodeId);
      const label = getPeriodLabel(item.periodName);

      if (!byYear[year]) byYear[year] = { ...item, year };
      const row = byYear[year];

      for (const key in item) {
        const value = item[key];

        if (key.startsWith('TOTAL_measure_')) {
          row[key] = value;
          continue;
        }

        if (!key.startsWith('ID_measure_')) continue;

        const parts = key.split('_');
        if (parts.length !== 5) continue;

        const measureIdSplit = parts[2];
        const nodeIdSplit = parts[4];

        if (nodeIdSplit !== nodeId || !measureIdSplit) continue;

        row[`ID_measure_${measureIdSplit}_${label}_${nodeId}`] = value;
      }
    }

    for (const yr of Object.keys(byYear)) {
      const row = byYear[yr];
      const keys = Object.keys(row);

      const totalKeys = keys.filter(k => k.startsWith('TOTAL_measure_'));
      for (const totalKey of totalKeys) {
        const measureId = extractTotalMeasureId(totalKey);
        if (measureId == null) continue;

        const idPrefix = `ID_measure_${measureId}_`;
        let sum = 0;
        for (const k of keys) {
          if (k.startsWith(idPrefix)) {
            sum += row[k].rowAmount;
          }
        }
        row[totalKey].rowAmount = sum;
      }
    }

    return Object.values(byYear).sort((a: any, b: any) => a.year - b.year);
  };

  const CashflowCellRender = (column) => {
    return (props) => <CashflowCell {...props} handleCashflowOpen={openCashflowDialog}
                                    boardId={params.boardId || boardData.boardId}
                                    scale={column.scale} columnName={column.name} selectedCurrency={selectedCurrency}
                                    singlePeriod={false}
                                    investorObjectId={params.investorId || widget.investorObjectId} />;
  };

  const NonCashflowRender = (column) => {
    return (props) => <NumericCell scale={column.scale} {...props} />;
  };

  useEffect(() => {
    setLoading(true);
    let wSetup = widgetSetup(widget);

    if (wSetup) {
      setGridSetup(wSetup);
      setGridColumns(wSetup.definition);
    }

    setLocalCurrency(selectedCurrency);

    if (selectedObject && selectedPeriod && selectedCurrency) {
      const { nodes } = defineNodes(widget, selectedObject);
      setNodes(nodes);

      if (scenarios) {
        setWidgetCurrencies(generateCurrencies(widget, nodes, scenarios));
      }

      fetchNodeData({
        ...widget,
        investorObjectId: widget.investorObjectId || parseInt(params.investorId),
      }, nodes, measuresForFetch(wSetup), selectedPeriod, selectedCurrency.currency || '', false).then((data) => {
        return {
          measureValues: data,
          staticValues: [],
        };
      }).then((res) => {
        let data = prepareAnnualGridData(wSetup, res?.measureValues, nodes);
        setGridData(data);
        setLoading(false);
      })
        .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  }, [selectedObject, selectedPeriod, selectedCurrency]);

  useEffect(() => {
    const periodColumns = extractPeriodColumns(gridData);
    setPeriodicityColumns(periodColumns);
    const transformed = transformData(gridData);

    //@ts-ignore
    setResultState(process(transformed, initialDataState));
    setDataState(initialDataState);
  }, [gridData]);

  useEffect(() => {
    filterCtx.setGridWidgetFilterState(dataState);
  }, [dataState]);

  useEffect(() => {
    if (notes) {
      filterNotes(notes);
    }
  }, [notes, widget, gridSetup]);

  return window.innerWidth < 960 && gridSetup?.hideOnMobile ? null : (
    <WidgetWrapper
      widget={widget}
      localCurrency={localCurrency}
      widgetCurrencies={widgetCurrencies}
      handleLocalCurrencyChange={handleLocalCurrencyChange}
      notes={gridNotes}
      moveWidget={moveWidget}
      removeWidget={removeWidget}
      selectedObject={selectedObject}
      customisation={customisation}
      handleLink={handleLink}
      content={content}
      homeContent={homeContent}
      widgetView={widgetView}
      handleUnpinFromBoardCallback={handleUnpinWidget}
      handlePinCallback={handlePinWidget}
      handleUnpin={handleUnpinTrigger}
      unpinId={unpinId}
      renderIcons={<Icon onClick={onExcelExport} name='far fa-file-excel' className='widget-icon pointer' />}
    >
      {loading ? (
        <ContentLoading />
      ) : (
        <div className='grid-widget-wrapper'>
          <ExcelExport data={resultState.data} ref={exportRef} fileName={replaceDotsWithUnderscores(widget.name) || t('EXPORT')}>
            <Grid
              data={resultState.data}
              {...dataState}
              resizable={true}
              onDataStateChange={onDataStateChange}
              sortable={true}
              className={`numeric-table-style-wrapper k-grid-without-tabs annual-grid`}
              scrollable={'scrollable'}>
              <GridColumn field='year' title={t('WIDGET.YEAR')} width={100} locked />
              {periodicityColumns.map((periodicity, pKey) => {
                if (gridColumns?.length > 1) return (
                  <GridColumn title={periodicity.periodLabel} key={`p_${pKey}`}>
                    {gridColumns.map((col, cKey) => {
                      return (
                        <GridColumn
                          key={`m_${cKey}`}
                          title={col.name}
                          width={200}
                          field={getFieldNameFor(col.measureId, periodicity.periodLabel, periodicity.nodeId)}
                          cell={
                            col.viewCashflows
                              ? CashflowCellRender(col)
                              : NonCashflowRender(col)
                          }
                        />
                      );
                    })}
                  </GridColumn>
                );
                else if (gridColumns && gridColumns[0]) {
                  const column = gridColumns && gridColumns[0];
                  return (
                    <GridColumn
                      title={periodicity.periodLabel}
                      field={getFieldNameFor(column?.measureId, periodicity.periodLabel, periodicity.nodeId)}
                      width={200} key={pKey}
                      cell={column?.viewCashflows ? CashflowCellRender(column) : NonCashflowRender(column)}
                    />
                  );
                }
              })}
              {gridColumns?.length > 1 ? (
                <GridColumn title={t('WIDGET.TOTAL')}>
                  {gridColumns.map((col, cKey) => {
                      return col.total && (
                        <GridColumn
                          key={`m_${cKey}`}
                          title={col.name}
                          width={200}
                          field={getTotalFieldNameFor(col.measureId)}
                          cell={NonCashflowRender(col)}
                        />
                      );
                    },
                  )}
                </GridColumn>
              ) : <GridColumn
                title={t('TOTAL')}
                width={200}
                field={getTotalFieldNameFor(gridColumns[0]?.measureId)}
                cell={NonCashflowRender(gridColumns[0])}
              />}
            </Grid>
          </ExcelExport>
        </div>
      )}
    </WidgetWrapper>
  );
};

export default AnnualGridWidget;
